import { Download, Maximize2, Minimize2, ZoomOut } from 'lucide-react';

interface ChartControlsProps {
  isFullscreen: boolean;
  onResetZoom: () => void;
  onToggleFullscreen: () => void;
  onDownload: () => void;
  showMeanAndConfidence?: boolean;
  onToggleMeanConfidence?: () => void;
  showFullscreen?: boolean;
}

export function ChartControls({
  isFullscreen,
  onResetZoom,
  onToggleFullscreen,
  onDownload,
  showMeanAndConfidence,
  onToggleMeanConfidence,
  showFullscreen = true,
}: ChartControlsProps) {
  return (
    <div className="flex justify-end gap-2 mb-2">
      {onToggleMeanConfidence && showMeanAndConfidence !== undefined && (
        <button
          onClick={onToggleMeanConfidence}
          className={`p-1.5 text-sm rounded group relative ${showMeanAndConfidence ? 'bg-pink-500 hover:bg-pink-600' : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'}`}
          aria-label={showMeanAndConfidence ? 'Hide Confidence Band' : 'Show Confidence Band'}
          data-ph-capture-attribute="btn-chart-toggle-mean-confidence"
        >
          {showMeanAndConfidence ? (
            <svg
              viewBox="0 0 28 32"
              className="w-4 h-4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Two vertical bars, edge-to-edge */}
              <line
                x1="0"
                y1="4"
                x2="0"
                y2="28"
                stroke="white"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
              <line
                x1="28"
                y1="4"
                x2="28"
                y2="28"
                stroke="white"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
              {/* Double-headed horizontal arrow, true arrowheads */}
              <line
                x1="5"
                y1="16"
                x2="23"
                y2="16"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              {/* Left arrowhead */}
              <line
                x1="4"
                y1="16"
                x2="9"
                y2="11"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <line
                x1="4"
                y1="16"
                x2="9"
                y2="21"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              {/* Right arrowhead */}
              <line
                x1="24"
                y1="16"
                x2="19"
                y2="11"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <line
                x1="24"
                y1="16"
                x2="19"
                y2="21"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg
              viewBox="0 0 28 32"
              className="w-4 h-4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Two vertical bars, edge-to-edge */}
              <line
                x1="0"
                y1="4"
                x2="0"
                y2="28"
                stroke="black"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
              <line
                x1="28"
                y1="4"
                x2="28"
                y2="28"
                stroke="black"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
              {/* Double-headed horizontal arrow, true arrowheads */}
              <line
                x1="5"
                y1="16"
                x2="23"
                y2="16"
                stroke="black"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              {/* Left arrowhead */}
              <line
                x1="4"
                y1="16"
                x2="9"
                y2="11"
                stroke="black"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <line
                x1="4"
                y1="16"
                x2="9"
                y2="21"
                stroke="black"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              {/* Right arrowhead */}
              <line
                x1="24"
                y1="16"
                x2="19"
                y2="11"
                stroke="black"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <line
                x1="24"
                y1="16"
                x2="19"
                y2="21"
                stroke="black"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          )}
          <span className="absolute hidden group-hover:block right-0 top-full mt-1 px-1 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
            {showMeanAndConfidence ? 'Hide Confidence Band' : 'Show Confidence Band'}
          </span>
        </button>
      )}
      <button
        onClick={onDownload}
        className="p-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded group relative"
        aria-label="Download Chart"
        data-ph-capture-attribute="btn-chart-download"
      >
        <Download className="w-4 h-4" />
        <span className="absolute hidden group-hover:block right-0 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
          Download PNG
        </span>
      </button>
      {showFullscreen && (
        <button
          onClick={onToggleFullscreen}
          className="p-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded group relative"
          aria-label="Toggle Fullscreen"
          data-ph-capture-attribute="btn-chart-fullscreen"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          <span className="absolute hidden group-hover:block right-0 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </span>
        </button>
      )}
      <button
        onClick={onResetZoom}
        className="p-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded group relative"
        aria-label="Reset Zoom"
        data-ph-capture-attribute="btn-chart-reset-zoom"
      >
        <ZoomOut className="w-4 h-4" />
        <span className="absolute hidden group-hover:block right-0 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
          Reset Zoom
        </span>
      </button>
    </div>
  );
}
